import { Layout } from '@/components/Layout';
import { ContactForm } from '@/components/ContactForm';
import { SEO } from '@/components/SEO';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { Shield, Clock, CheckCircle, MapPin, Phone, Mail } from 'lucide-react';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Contact() {
  const contactInfo: Array<{icon: any; titre: string; contenu: string; details: string; link?: string}> = [
    {
      icon: MapPin,
      titre: 'Adresse',
      contenu: '39 rue Sainte-Marie',
      details: 'Gatineau, QC J8Y 4A2',
      link: 'https://maps.google.com/?q=39+rue+Sainte-Marie+Gatineau+J8Y+4A2',
    },
    {
      icon: Phone,
      titre: 'Prise de rendez-vous',
      contenu: 'Réserver en ligne',
      details: 'Système de réservation Jane App',
      link: 'https://expertisefamiliale.janeapp.com',
    },
    {
      icon: Mail,
      titre: 'Courriel',
      contenu: 'info@expertisefamiliale.ca',
      details: 'Réponse sous 24-48 heures',
      link: 'mailto:info@expertisefamiliale.ca',
    },
  ];

  const reassuranceBadges = [
    {
      icon: Shield,
      titre: 'Confidentialité garantie',
      description: 'Toutes vos informations sont protégées',
    },
    {
      icon: Clock,
      titre: 'Réponse rapide',
      description: 'Nous vous contactons sous 24-48 heures',
    },
    {
      icon: CheckCircle,
      titre: 'Première consultation',
      description: 'Évaluation de vos besoins sans engagement',
    },
  ];

  const heuresOuverture = [
    { jour: 'Lundi - Vendredi', heures: '9h00 - 17h00' },
    { jour: 'Samedi', heures: 'Sur rendez-vous' },
    { jour: 'Dimanche', heures: 'Fermé' },
  ];

  return (
    <Layout>
      <SEO
        title="Nous contacter — Prendre rendez-vous"
        description="Contactez le Centre d'expertise familiale de l'Outaouais : 39 rue Sainte-Marie, Gatineau J8Y 4A2. Prenez rendez-vous en ligne via Jane App. info@expertisefamiliale.ca"
        keywords="contact expertise familiale Gatineau, rendez-vous médiation familiale, adresse centre expertise Outaouais, prise de rendez-vous en ligne"
        canonical="/contact"
      />
      <div className="min-h-screen bg-background">
        <motion.section
          className="relative py-24 overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src={IMAGES.CONSULTATION_3}
              alt="Contact"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center mb-16"
              variants={staggerItem}
            >
              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground"
                variants={fadeInUp}
              >
                Nous sommes là pour vous accompagner
              </motion.h1>
              <motion.p
                className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
                variants={fadeInUp}
              >
                Contactez-nous pour discuter de votre situation en toute confidentialité.
                Notre équipe est à votre écoute pour vous guider vers les solutions adaptées à vos besoins.
              </motion.p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16"
              variants={staggerContainer}
            >
              {reassuranceBadges.map((badge, index) => {
                const Icon = badge.icon;
                return (
                  <motion.div
                    key={index}
                    className="bg-card border border-border rounded-xl p-6 text-center"
                    variants={staggerItem}
                    whileHover={{ scale: 1.02 }}
                    transition={springPresets.snappy}
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-foreground">{badge.titre}</h3>
                    <p className="text-sm text-muted-foreground">{badge.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </motion.section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={springPresets.gentle}
              >
                <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
                    Envoyez-nous un message
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Remplissez le formulaire ci-dessous et nous vous contacterons dans les plus brefs délais.
                  </p>
                  <ContactForm />
                </div>
              </motion.div>

              <motion.div
                className="space-y-8"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={springPresets.gentle}
              >
                <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold mb-6 text-foreground">Coordonnées</h2>
                  <div className="space-y-6">
                    {contactInfo.map((info, index) => {
                      const Icon = info.icon;
                      return (
                        <div key={index} className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground mb-1">{info.titre}</h3>
                            {info.link ? (
                              <a
                                href={info.link}
                                target={info.link.startsWith('http') ? '_blank' : undefined}
                                rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                                className="text-foreground font-medium hover:text-primary transition-colors block"
                              >
                                {info.contenu}
                              </a>
                            ) : (
                              <p className="text-foreground font-medium">{info.contenu}</p>
                            )}
                            <p className="text-sm text-muted-foreground">{info.details}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold mb-6 text-foreground">Heures d'ouverture</h2>
                  <div className="space-y-4">
                    {heuresOuverture.map((horaire, index) => (
                      <div key={index} className="flex justify-between items-center py-3 border-b border-border last:border-0">
                        <span className="font-medium text-foreground">{horaire.jour}</span>
                        <span className="text-muted-foreground">{horaire.heures}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold mb-4 text-foreground">Prise de rendez-vous en ligne</h2>
                  <p className="text-muted-foreground mb-6">
                    Réservez votre consultation directement en ligne via notre système sécurisé Jane App. Choisissez la date et l'heure qui vous conviennent.
                  </p>
                  <a
                    href="https://expertisefamiliale.janeapp.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] text-center"
                  >
                    Réserver maintenant
                  </a>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    Système de réservation sécurisé
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-5xl mx-auto bg-card border border-border rounded-2xl overflow-hidden shadow-sm"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <div className="aspect-video w-full bg-muted relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2800.8!2d-75.7064!3d45.4292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDI1JzQ1LjEiTiA3NcKwNDInMjMuMCJX!5e0!3m2!1sfr!2sca!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Carte de localisation - 39 rue Sainte-Marie, Gatineau"
                  className="absolute inset-0"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2 text-foreground">Notre adresse</h3>
                <p className="text-foreground font-medium mb-2">39 rue Sainte-Marie, Gatineau, QC J8Y 4A2</p>
                <p className="text-muted-foreground">
                  Nous desservons principalement la région de l'Outaouais, incluant Gatineau et les municipalités environnantes.
                  Selon la nature du mandat, nous pouvons également offrir nos services dans d'autres régions du Québec.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-accent/50">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Prêt à faire le premier pas?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Nous comprenons que contacter un professionnel peut être une étape difficile.
                Soyez assuré que votre démarche sera accueillie avec respect, empathie et professionnalisme.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://expertisefamiliale.janeapp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-8 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Prendre rendez-vous
                </a>
                <a
                  href="mailto:info@expertisefamiliale.ca"
                  className="inline-flex items-center justify-center bg-card text-foreground border border-border hover:bg-muted font-semibold py-3 px-8 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Envoyez un courriel
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
